import { AREA_MARGINS, BACKGROUND_IMAGE_DPI, BACKGROUND_IMAGE_H, BACKGROUND_IMAGE_W } from "@/web-components/import-wizard-v2/constants";
import { PrintArea } from "@/web-components/import-wizard-v2/typings/common";
import { ProductPrintArea } from "@midocean/webapps-shared-domain";
import { mm2px, px2mm } from "./common";

export function translatePolygonToCenter(polygon: {x:number, y:number}[]){

  const centerX = BACKGROUND_IMAGE_W / 2;
  const centerY = BACKGROUND_IMAGE_H / 2;

  const centroidX = polygon.reduce((sum, vertex) => sum + vertex.x, 0) / polygon.length;
  const centroidY = polygon.reduce((sum, vertex) => sum + vertex.y, 0) / polygon.length;

  const translateX = centerX - centroidX;
  const translateY = centerY - centroidY;

  return polygon.map(vertex => ({x: vertex.x + translateX, y: vertex.y + translateY}));
}

export function scaleRectanglePrintPosition(rectangle: {x: number, y: number, width: number, height: number, rotation: number}){

  let width, height, x, y, scaleFactor = 0;
  const rotation = rectangle.rotation;

  const areaRatio = rectangle.width / rectangle.height;
  const bgImageRation = BACKGROUND_IMAGE_W / BACKGROUND_IMAGE_H;

  if(rectangle.rotation === 0){

    if(areaRatio > bgImageRation){
      width = BACKGROUND_IMAGE_W - 2 * AREA_MARGINS;
      scaleFactor = width / rectangle.width;
      height = rectangle.height * scaleFactor;
    }else{
      height = BACKGROUND_IMAGE_H - 2 * AREA_MARGINS;
      scaleFactor = height / rectangle.height;
      width = rectangle.width * scaleFactor;
    }

    x = (BACKGROUND_IMAGE_W - width) / 2;
    y = (BACKGROUND_IMAGE_H - height) / 2;

  } else {

    const boundingBoxW = rectangle.width * Math.abs(Math.cos(rectangle.rotation)) + rectangle.height * Math.abs(Math.sin(rectangle.rotation));
    const boundingBoxH = rectangle.width * Math.abs(Math.sin(rectangle.rotation)) + rectangle.height * Math.abs(Math.cos(rectangle.rotation));

    scaleFactor = Math.min((BACKGROUND_IMAGE_W - 2 * AREA_MARGINS) / boundingBoxW, (BACKGROUND_IMAGE_H - 2 * AREA_MARGINS) / boundingBoxH);

    if(scaleFactor < 1){
      width = rectangle.width;
      height = rectangle.height;
    }else{
      width = scaleFactor * rectangle.width;
      height = scaleFactor * rectangle.height;
    }

    x = (BACKGROUND_IMAGE_W - width) / 2;
    y = (BACKGROUND_IMAGE_H - height) / 2;
  }
 
  return { x, y, width, height, rotation, scaleFactor }
}

export function scaleEllipsePrintPosition(ellipse: {x: number, y: number, rx: number, ry: number, rotation: number}){

  const rectangle = {
    x: 0,
    y: 0,
    width: 2 * ellipse.rx,
    height: 2 * ellipse.ry,
    rotation: ellipse.rotation
  };

  const scaledBoundingRect = scaleRectanglePrintPosition(rectangle);

  return { x: BACKGROUND_IMAGE_W / 2, y: BACKGROUND_IMAGE_H / 2, rx: scaledBoundingRect.width / 2,
     ry: scaledBoundingRect.height / 2, rotation: scaledBoundingRect.rotation, scaleFactor: scaledBoundingRect.scaleFactor };
  
}

export function scalePolygonPrintPosition(polygon: { points: {x: number, y:number}[], rotation: number}){

  const minX = Math.min(...polygon.points.map(x=> x.x));
  const maxX = Math.max(...polygon.points.map(x=> x.x));
  const minY = Math.min(...polygon.points.map(x=> x.y));
  const maxY = Math.max(...polygon.points.map(x=> x.y));

  const boundingRect = {
    x: minX,
    y: maxY,
    width: maxX - minX,
    height: maxY - minY,
    rotation: 0
  };

  const scaledBoundingRect = scaleRectanglePrintPosition(boundingRect);
  const scaleFactor = scaledBoundingRect.scaleFactor;

  const scaledPoints = polygon.points.map(p => ({x: p.x * scaleFactor, y: p.y * scaleFactor}));

  const scaledMinX = Math.min(...scaledPoints.map(x=> x.x));
  const scaledMinY = Math.min(...scaledPoints.map(x=> x.y));

  const dx = scaledBoundingRect.x - scaledMinX;
  const dy = scaledBoundingRect.y - scaledMinY;

  const points = scaledPoints.map(v => ({ x: v.x + dx, y: v.y + dy }));

  return { points, scaleFactor };
}

export function getShapePositionInAreainPt(shape: { x: number, y: number, height: number, width: number }, area: PrintArea){

  if(area.kind === 'Rectangle'){
    const xInArea = shape.x - area.x;
    const yInArea = shape.y - area.y;
  
    const xInAreaPt = (100 * xInArea) / area.width;
    const yInAreaPt = (100 * yInArea) / area.height;
    const widthInAreaPt = (100 * shape.width) / area.width;
    const heightInAreaPt = (100 * shape.height) / area.height;
  
    return { x: xInAreaPt, y: yInAreaPt, width: widthInAreaPt, height: heightInAreaPt };
  }

  if(area.kind === 'Ellipse'){
    const leftX = area.x - area.rx;
    const leftY = area.y - area.ry;

    const xInArea = shape.x - leftX;
    const yInArea = shape.y - leftY;

    const areaW = area.rx * 2;
    const areaH = area.ry * 2;

    const xInAreaPt = (100 * xInArea) / areaW;
    const yInAreaPt = (100 * yInArea) / areaH;
    const widthInAreaPt = (100 * shape.width) / areaW;
    const heightInAreaPt = (100 * shape.height) / areaH;

    return { x: xInAreaPt, y: yInAreaPt, width: widthInAreaPt, height: heightInAreaPt };
  }

  if(area.kind === 'Polygon'){
    const minX = Math.min(...area.points.map(x=> x.x));
    const maxX = Math.max(...area.points.map(x=> x.x));
    const minY = Math.min(...area.points.map(x=> x.y));
    const maxY = Math.max(...area.points.map(x=> x.y));

    const areaW = maxX - minX;
    const areaH = maxY - minY;
    
    const xInArea = shape.x - minX;
    const yInArea = shape.y - maxY;

    const xInAreaPt = (100 * xInArea) / areaW;
    const yInAreaPt = (100 * yInArea) / areaH;
    const widthInAreaPt = (100 * shape.width) / areaW;
    const heightInAreaPt = (100 * shape.height) / areaH;

    return { x: xInAreaPt, y: yInAreaPt, width: widthInAreaPt, height: heightInAreaPt };
  }

  return undefined;
}

export function getShapePositionOnProductInMM(positionInPt: { x: number, y: number, height: number, width: number }, productArea: ProductPrintArea){

  const xInMM = productArea.widthMm * (positionInPt.x / 100);
  const yInMM = productArea.heightMm * (positionInPt.y / 100);
  const widthOnProductMM = productArea.widthMm * (positionInPt.width / 100);
  const heightOnProductMM = productArea.heightMm * (positionInPt.height / 100);

  if(productArea.shapeType === 'Rectangle' || productArea.shapeType === 'Ellipse'){
    const leftPoint = productArea.productPrintAreaPoints.find(x => x.sequenceNo === 1);

    if(leftPoint){
      const xOnProductMM = leftPoint.distanceFromLeftEdge + xInMM;
      const yOnProductMM = leftPoint.distanceFromTop + yInMM;
  
      return { xOnProductMM, yOnProductMM, widthOnProductMM, heightOnProductMM };
    }
  }

  if(productArea.shapeType === 'Polygon'){
    
    const minX = Math.min(...productArea.productPrintAreaPoints.map(x=> x.distanceFromLeftEdge));
    const maxY = Math.max(...productArea.productPrintAreaPoints.map(x=> x.distanceFromTop));

    const maxX = Math.max(...productArea.productPrintAreaPoints.map(x=> x.distanceFromLeftEdge));
    const minY = Math.min(...productArea.productPrintAreaPoints.map(x=> x.distanceFromTop));

    const areaW = maxX - minX;
    const areaH = maxY - minY;

    const xInMM = areaW * (positionInPt.x / 100);
    const yInMM = areaH * (positionInPt.y / 100);

    const xOnProductMM = minX + xInMM;
    const yOnProductMM = maxY + yInMM;

    const widthOnProductMM = areaW * (positionInPt.width / 100);
    const heightOnProductMM = areaH * (positionInPt.height / 100);

    return { xOnProductMM, yOnProductMM, widthOnProductMM, heightOnProductMM };
  }

  return undefined;
}

export function getScaledProductImageDPI(originalImageWidthPx: number, originalImageHeightPx: number, originalDPI: number, scaledValue: number){
  if(originalImageHeightPx > originalImageWidthPx){
    return originalDPI / (originalImageHeightPx / scaledValue);
  }
  return originalDPI / (originalImageWidthPx / scaledValue);
}